import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const TOKEN_KEY = "vini_admin_token";

// Public, unauthenticated client — used by the live website.
export const publicApi = axios.create({ baseURL: API_BASE });

// Authenticated client — used by the Admin Portal. Automatically attaches
// the stored JWT and redirects to the login screen if a request comes back
// unauthorized (expired/invalid session).
export const adminApi = axios.create({ baseURL: API_BASE });

adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

adminApi.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem("vini_admin_username");
      const path = window.location.pathname;
      if (path.startsWith("/admin") && path !== "/admin/login") {
        window.location.href = "/admin/login";
      }
    }
    return Promise.reject(err);
  }
);

// ---------------------------------------------------------------------------
// Public product API (consumed by the live website sections)
// ---------------------------------------------------------------------------
export async function fetchProducts(category) {
  const { data } = await publicApi.get("/products", {
    params: category ? { category } : {},
  });
  return data;
}

/**
 * Paginated product fetch for the full catalog page — supports category,
 * purity, and skip/limit so large catalogs (hundreds+ items) load in pages
 * instead of all at once.
 */
export async function fetchProductsPage({ category, purity, search, skip = 0, limit = 24 } = {}) {
  const params = { skip, limit };
  if (category) params.category = category;
  if (purity) params.purity = purity;
  if (search) params.search = search;
  const { data } = await publicApi.get("/products", { params });
  return data;
}

export async function fetchProduct(id) {
  const { data } = await publicApi.get(`/products/${id}`);
  return data;
}

export async function fetchSettings() {
  const { data } = await publicApi.get("/settings");
  return data;
}

export async function updateSettings(payload) {
  const { data } = await adminApi.put("/admin/settings", payload);
  return data;
}

export async function fetchProductsCount({ category, purity, search } = {}) {
  const params = {};
  if (category) params.category = category;
  if (purity) params.purity = purity;
  if (search) params.search = search;
  const { data } = await publicApi.get("/products/count", { params });
  return data.count;
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------
export async function loginAdmin(username, password) {
  const { data } = await publicApi.post("/auth/login", { username, password });
  return data;
}

export async function verifyAdmin() {
  const { data } = await adminApi.get("/auth/verify");
  return data;
}

// ---------------------------------------------------------------------------
// Admin product management
// ---------------------------------------------------------------------------
export async function fetchAdminProducts(category) {
  const { data } = await adminApi.get("/admin/products", {
    params: category ? { category } : {},
  });
  return data;
}

export async function createProduct(payload) {
  const { data } = await adminApi.post("/admin/products", payload);
  return data;
}

export async function updateProduct(id, payload) {
  const { data } = await adminApi.put(`/admin/products/${id}`, payload);
  return data;
}

export async function deleteProduct(id) {
  const { data } = await adminApi.delete(`/admin/products/${id}`);
  return data;
}

export async function uploadMedia(file, onProgress) {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await adminApi.post("/admin/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (evt) => {
      if (onProgress && evt.total) {
        onProgress(Math.round((evt.loaded / evt.total) * 100));
      }
    },
  });
  return data;
}

/**
 * Resolves a media URL returned by the API into a fully usable <img>/<video>
 * src. Backend-uploaded files come back as relative paths like
 * "/api/uploads/xyz.png" and need the backend origin prefixed. Static
 * frontend assets ("/images/...") and absolute URLs are returned as-is.
 */
export function resolveMediaUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
}
